import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import ScopingAILayout from '../../components/RevisrLayout';
import { Stepper } from '@foundation/components/stepper';
import { Button } from '@foundation/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@foundation/components/ui/card';
import { Badge } from '@foundation/components/ui/badge';
import { useUser } from '@foundation/contextapi/UserContext';
import { useWorkspaceContext } from '@foundation/hooks/useWorkspaceContext';
import { useToast } from '@foundation/hooks/use-toast';
import BrandingService from '@foundation/services/brandingService';
import GenerationStream from '../../components/create-policy/GenerationStream';
import OrgContextCard from '../../components/create-policy/OrgContextCard';
import { generatePolicy, saveContent, updateStep } from '../../services/policyGenerationService';
import { supabaseRevisr } from '../../utils/supabaseRevisrClient';
import type { PolicyGenerationStreamEvent } from '../../types/policyGeneration';
import {
  ArrowLeft,
  ArrowRight,
  Square,
  Loader2,
  CheckCircle,
  Sparkles,
  FileText,
} from 'lucide-react';

const CREATE_POLICY_STEPS = [
  { label: 'Details', description: 'Enter policy info' },
  { label: 'Generate', description: 'AI generates draft' },
  { label: 'Edit', description: 'Review & refine' },
  { label: 'Export', description: 'Preview & export' },
];

type GenerationStatus = 'idle' | 'generating' | 'completed' | 'error' | 'stopped';

export default function PolicyGeneratePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useUser();
  const { currentWorkspace } = useWorkspaceContext();
  const { toast } = useToast();

  const orgId = currentWorkspace?.id || '';
  const userId = user?.id || '';

  // URL-driven ID (primary), sessionStorage (fallback)
  const urlPolicyId = searchParams.get('id') || '';
  const sessionPolicyId = sessionStorage.getItem('create_policy_policy_review_id') || '';
  const policyReviewId = urlPolicyId || sessionPolicyId;

  // Session state for generation params
  const [templateId, setTemplateId] = useState(sessionStorage.getItem('create_policy_template_id') || '');
  const [templateName, setTemplateName] = useState(sessionStorage.getItem('create_policy_template_name') || '');
  const [policyName, setPolicyName] = useState(sessionStorage.getItem('create_policy_policy_name') || '');
  const [additionalPrompt, setAdditionalPrompt] = useState(sessionStorage.getItem('create_policy_additional_prompt') || '');
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Generation state
  const [content, setContent] = useState('');
  const [status, setStatus] = useState<GenerationStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const hasStartedRef = useRef(false);
  const hasPersistedRef = useRef(false);

  // Branding state
  const [branding, setBranding] = useState<{
    logoUrl?: string;
    fontFamily?: string;
    primaryColor?: string;
    secondaryColor?: string;
    defaultColor?: string;
  }>({});

  // Sync URL ID to sessionStorage so other parts of the flow can use it
  useEffect(() => {
    if (urlPolicyId) {
      sessionStorage.setItem('create_policy_policy_review_id', urlPolicyId);
    }
  }, [urlPolicyId]);

  // Load policy data from DB if sessionStorage is empty (resume scenario)
  useEffect(() => {
    if (!policyReviewId || templateId) return;

    let isMounted = true;

    const loadFromDB = async () => {
      setIsLoadingData(true);
      try {
        const { data, error: fetchError } = await supabaseRevisr
          .from('revisr_final_policies')
          .select('policy_name, template_store_id, generation_prompt')
          .eq('id', policyReviewId)
          .single();

        if (fetchError || !data) {
          console.error('Error loading policy data from DB:', fetchError);
          return;
        }

        if (isMounted) {
          if (data.policy_name) {
            setPolicyName(data.policy_name);
            sessionStorage.setItem('create_policy_policy_name', data.policy_name);
          }
          if (data.template_store_id) {
            setTemplateId(data.template_store_id);
            sessionStorage.setItem('create_policy_template_id', data.template_store_id);
          }
          if (data.generation_prompt) {
            setAdditionalPrompt(data.generation_prompt);
            sessionStorage.setItem('create_policy_additional_prompt', data.generation_prompt);
          }
        }
      } catch (err) {
        console.error('Error loading policy data:', err);
      } finally {
        if (isMounted) setIsLoadingData(false);
      }
    };

    loadFromDB();
    return () => { isMounted = false; };
  }, [policyReviewId, templateId]);

  // Fetch organization branding - only once
  useEffect(() => {
    if (!orgId) return;

    let isMounted = true;

    const fetchBranding = async () => {
      try {
        const orgBranding = await BrandingService.getOrganizationBranding(orgId);
        if (orgBranding && isMounted) {
          setBranding({
            logoUrl: orgBranding.logo_url ? BrandingService.getLogoUrlWithCacheBusting(orgBranding.logo_url) : undefined,
            fontFamily: orgBranding.font_family || undefined,
            primaryColor: orgBranding.primary_color || undefined,
            secondaryColor: orgBranding.secondary_color || undefined,
            defaultColor: orgBranding.font_color || undefined,
          });
        }
      } catch (err) {
        console.error('Error fetching branding:', err);
      }
    };

    fetchBranding();

    return () => {
      isMounted = false;
    };
  }, [orgId]);

  // Start generation on mount
  const startGeneration = useCallback(async () => {
    if (!policyReviewId || !templateId || !orgId || !userId) {
      setError('Missing required data. Please go back and fill in the details.');
      setStatus('error');
      return;
    }

    try {
      setStatus('generating');
      setContent('');
      setError(null);

      const controller = new AbortController();
      abortControllerRef.current = controller;

      let fullContent = '';

      let generationErrored = false;

      await generatePolicy(
        {
          templateId,
          templateName,
          policyName,
          additionalPrompt: additionalPrompt || undefined,
          orgId,
          userId,
          orgContext: {
            workspaceName: currentWorkspace?.name || undefined,
          },
        },
        (event: PolicyGenerationStreamEvent) => {
          if (controller.signal.aborted) return;

          switch (event.type) {
            case 'start':
              break;
            case 'content':
              fullContent += event.text;
              setContent(fullContent);
              break;
            case 'complete':
              fullContent = event.fullContent;
              setContent(fullContent);
              setStatus('completed');
              break;
            case 'error':
              generationErrored = true;
              setError(event.message);
              setStatus('error');
              break;
          }
        }
      );

      if (!generationErrored && !controller.signal.aborted) {
        setStatus('completed');
      }

      // Persist generated content immediately after successful generation
      if (!generationErrored && !controller.signal.aborted && fullContent && !hasPersistedRef.current) {
        hasPersistedRef.current = true;
        try {
          await saveContent(policyReviewId, fullContent, userId, orgId);
          await updateStep(policyReviewId, 2, userId, orgId, 'generated');
          sessionStorage.setItem('create_policy_generated_content', fullContent);
        } catch (persistErr) {
          console.error('Error persisting generated content:', persistErr);
          hasPersistedRef.current = false;
        }
      }
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') {
        setStatus('stopped');
        return;
      }
      console.error('Generation error:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to generate policy';
      setError(errorMsg);
      setStatus('error');
    }
  }, [policyReviewId, templateId, templateName, policyName, additionalPrompt, orgId, userId]);

  useEffect(() => {
    if (isLoadingData) return;
    if (!hasStartedRef.current) {
      hasStartedRef.current = true;
      startGeneration();
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [startGeneration, isLoadingData]);

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setStatus('stopped');
    }
  };

  const handleRetry = () => {
    hasStartedRef.current = false;
    hasPersistedRef.current = false;
    setContent('');
    setError(null);
    hasStartedRef.current = true;
    startGeneration();
  };

  const handleContinueToEdit = async () => {
    if (!content || !policyReviewId) return;

    try {
      setIsSaving(true);

      // If content wasn't auto-persisted (e.g. user stopped generation early),
      // save it now before navigating
      if (!hasPersistedRef.current) {
        await saveContent(policyReviewId, content, userId, orgId);
        await updateStep(policyReviewId, 2, userId, orgId, 'generated');
        sessionStorage.setItem('create_policy_generated_content', content);
        hasPersistedRef.current = true;
      }

      navigate(`/revisr/create-policy/edit?id=${policyReviewId}`);
    } catch (err) {
      console.error('Error saving generated content:', err);
      toast({
        title: 'Error',
        description: 'Failed to save generated content. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Redirect if no policy ID available from URL or session
  if (!policyReviewId) {
    return (
      <ScopingAILayout>
        <div className="min-h-screen bg-[#FCFBF4] flex items-center justify-center">
          <Card className="max-w-md">
            <CardContent className="pt-6 text-center">
              <FileText className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <h2 className="text-lg font-semibold text-gray-900 mb-2">No Policy Details Found</h2>
              <p className="text-sm text-gray-500 mb-4">
                Please fill in the policy details first before generating.
              </p>
              <Button
                onClick={() => navigate('/revisr/create-policy')}
                className="bg-[#33b864] hover:bg-[#2a9d54] text-white"
              >
                Go to Details
              </Button>
            </CardContent>
          </Card>
        </div>
      </ScopingAILayout>
    );
  }

  return (
    <ScopingAILayout>
      <div className="min-h-screen bg-[#FCFBF4]">
        {/* Header */}
        <motion.div
          className="bg-white/80 backdrop-blur-xl border-b border-white/20 shadow-sm"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/revisr/create-policy')}
                  className="gap-2 hover:bg-white/50 transition-all duration-300 group mb-2"
                >
                  <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform duration-300" />
                  Back to Details
                </Button>
                <h1 className="text-2xl font-bold text-gray-900">Generate Policy</h1>
                <p className="text-sm text-gray-500 mt-1">
                  AI is generating your <strong>{policyName}</strong> using the selected template
                </p>
              </div>

              {/* Status Badge */}
              <div className="flex items-center gap-3">
                {status === 'generating' && (
                  <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-200">
                    <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                    Generating...
                  </Badge>
                )}
                {status === 'completed' && (
                  <Badge variant="secondary" className="bg-green-50 text-green-700 border-green-200">
                    <CheckCircle className="h-3 w-3 mr-1.5" />
                    Complete
                  </Badge>
                )}
                {status === 'stopped' && (
                  <Badge variant="secondary" className="bg-amber-50 text-amber-700 border-amber-200">
                    <Square className="h-3 w-3 mr-1.5" />
                    Stopped
                  </Badge>
                )}
                {status === 'error' && (
                  <Badge variant="secondary" className="bg-red-50 text-red-700 border-red-200">
                    Generation Error
                  </Badge>
                )}
              </div>
            </div>

            {/* Stepper */}
            <Stepper currentStep={2} steps={CREATE_POLICY_STEPS} />
          </div>
        </motion.div>

        {/* Main Content */}
        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Left Sidebar - Context Info */}
            <div className="lg:col-span-1 space-y-4">
              {/* Policy Info Card */}
              <Card className="border border-gray-200 bg-white shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Sparkles className="h-3.5 w-3.5 text-[#33b864]" />
                    Generation Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-xs">
                  <div>
                    <span className="text-gray-500">Policy:</span>
                    <p className="font-medium text-gray-900 truncate">{policyName}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Template:</span>
                    <p className="font-medium text-gray-900 truncate">{templateName}</p>
                  </div>
                  {additionalPrompt && (
                    <div>
                      <span className="text-gray-500">Instructions:</span>
                      <p className="text-gray-700 line-clamp-3">{additionalPrompt}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Org Context */}
              <OrgContextCard organizationId={orgId} />
            </div>

            {/* Main Area - Generation Stream */}
            <div className="lg:col-span-3">
              <Card className="border border-gray-200 bg-white shadow-sm">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <FileText className="h-4 w-4 text-[#33b864]" />
                    Generated Policy
                  </CardTitle>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2">
                    {status === 'generating' && (
                      <Button
                        onClick={handleStop}
                        variant="outline"
                        size="sm"
                        className="border-red-200 text-red-600 hover:bg-red-50"
                      >
                        <Square className="h-3 w-3 mr-1.5" />
                        Stop Generation
                      </Button>
                    )}

                    {(status === 'error' || status === 'stopped') && (
                      <Button
                        onClick={handleRetry}
                        variant="outline"
                        size="sm"
                      >
                        <Sparkles className="h-3 w-3 mr-1.5" />
                        Retry
                      </Button>
                    )}

                    {(status === 'completed' || (status === 'stopped' && content.length > 0)) && (
                      <Button
                        onClick={handleContinueToEdit}
                        disabled={isSaving}
                        className="bg-[#33b864] hover:bg-[#2a9d54] text-white"
                        size="sm"
                      >
                        {isSaving ? (
                          <>
                            <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            Continue to Edit
                            <ArrowRight className="h-3 w-3 ml-1.5" />
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="p-0">
                  <GenerationStream
                    content={content}
                    isStreaming={status === 'generating'}
                    error={error}
                    onRetry={handleRetry}
                    maxHeight="calc(100vh - 350px)"
                    branding={branding}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </motion.div>
      </div>
    </ScopingAILayout>
  );
}
